import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlValueAccessor,
    NgControl,
} from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { TranslateService } from '@ngx-translate/core';
import {
    FileDownloadService,
    FileUploadService,
    ModalService,
} from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import { identifier } from '@uipath/angular/utilities';
import { readFileContent } from '@utils/file.utils';
import { UI_QUEUE_SCHEMA_MAX_FILE_SIZE } from '@validators';

import {
    BehaviorSubject,
    Subject,
} from 'rxjs';


@Component({
    selector: 'ui-queue-schema',
    templateUrl: 'ui-queue-schema.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: UiQueueSchemaComponent,
        },
    ],
})
export class UiQueueSchemaComponent implements
    OnInit,
    OnDestroy,
    MatFormFieldControl<string | null>,
    ControlValueAccessor {

    @Input()
    public get value(): string | null {
        return this.currentValue;
    }
    public set value(state: string | null) {
        this.currentValue = state;
        this.stateChanges.next();
    }

    @Input()
    get placeholder() {
        return this.labelText;
    }
    set placeholder(plh) {
        this.labelText = plh;
        this.stateChanges.next();
    }

    public get empty() {
        return this.currentValue == null;
    }

    @Input()
    get required() {
        return this._required;
    }
    set required(req) {
        this._required = req;
        this.stateChanges.next();
    }

    private static _isValidJson(str: string) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }

    @Input() labelText = 'Value';
    @Input() schemaName = 'schemaName';
    @Input() disabled = false;
    @Input() currentValue!: string | null;
    @Input() initialFileName!: string;
    @Input() confirmUpload = false;

    public shouldLabelFloat = false;
    public id: string;
    public stateChanges = new Subject<void>();
    public focused = false;
    public errorState = false;

    @Output() valueChange = new EventEmitter<string>();

    public describedBy = '';

    public reading$ = new BehaviorSubject<boolean>(false);

    @Output()
    public reading = new EventEmitter<boolean>(false);

    public fileName = '';

    private _required = false;
    private _initialExisted = false;
    private _randomName: string;

    constructor(
        public ngControl: NgControl,
        protected _cd: ChangeDetectorRef,
        private _upload: FileUploadService,
        private _snack: UiSnackBarService,
        private _translate: TranslateService,
        private _downloadService: FileDownloadService,
        private _modal: ModalService,
        private _zone: NgZone,
    ) {
        this.ngControl.valueAccessor = this;
        this._randomName = identifier();
        this.id = 'ui-queue-schema-' + this._randomName;
    }

    ngOnInit() {
        this._initialExisted = !!this.currentValue;
        if (this._initialExisted) {
            this.fileName = this.initialFileName;
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }

    writeValue(value: string | null) {
        this.currentValue = value;
        this._cd.markForCheck();
        this.stateChanges.next();
    }

    public registerChange = (_: string | null) => { };

    public registerTouch = (_: string | null) => { };

    public registerOnChange(fn: () => {}) {
        this.registerChange = fn;
    }

    public registerOnTouched(fn: () => {}) {
        this.registerTouch = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
        this._cd.detectChanges();
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(_event: MouseEvent) { }

    public onClear() {
        this.fileName = '';
        this.writeValue(null);
        this.registerChange(null);
        if (!this._initialExisted) {
            this.ngControl.control!.markAsPristine();
        }
    }

    public async onFileChange(files: File[]) {
        this.reading$.next(true);
        this.reading.emit(true);
        try {
            if (!this._upload.isFileItemValid(files[0], null, UI_QUEUE_SCHEMA_MAX_FILE_SIZE)) {
                this._snack.error(
                    this._translate.instant(`queue_upsert_form_invalid_schema_file`, {
                        maxSize: '256KB',
                    }),
                );
                return;
            }

            const resultAsString = await readFileContent(files[0]);

            if (!UiQueueSchemaComponent._isValidJson(resultAsString)) {
                this._snack.error(
                    this._translate.instant(`queue_json_schema_not_valid`),
                );
                return;
            }

            this.writeValue(resultAsString);
            this.registerChange(resultAsString);
            this.fileName = files[0].name;
        } finally {
            this.reading$.next(false);
            this.reading.emit(false);
        }
    }

    public onDownload() {
        this._downloadService.downloadStringAsFile(
            `${this.schemaName}DataJsonFile.json`,
            'application/json',
            this.currentValue!,
        );
    }

    public confirmUploadDialog(event: Event) {
        if (this.confirmUpload) {
            const target = event.target! as HTMLInputElement;
            event.stopPropagation();
            this._modal.onConfirm(null,
                () => this._onOkay(target),
                () => {},
                this._translate.instant('queue_json_schema_confirm_upload'),
            );
        }
    }

    private _onOkay = (target: HTMLInputElement) => {
        this.confirmUpload = false;
        this._zone.runOutsideAngular(_ => target.click());
    }
}
